import type {
  CasdoorUser,
  AuthTokens,
  PSAuthConfig,
} from "@pineseed/gui/types/auth";
import {
  // secureStorage,
  createSecureStorage,
  SecureStorageKeys,
  // storeAuthTokens,
  getAuthTokens,
  clearAuthTokens,
  storePKCEVerifier,
  getPKCEVerifier,
  clearPKCEVerifier,
} from "@pineseed/gui/util/secure-storage";

const secureStorage = createSecureStorage();

// Extended window interface to avoid `any` usage in runtime-specific code.
type ExtensionMessage = {
  type: string;
  authState?: {
    user: CasdoorUser;
    tokens: AuthTokens;
  };
  isAuthenticated?: boolean;
  [key: string]: unknown;
};

type ExtendedWindow = Window & {
  __IVE_EXTENSION_MODE__?: boolean;
  __TAURI__?: unknown;
  isTauri?: boolean;
  __TAURI_METADATA__?: unknown;
  __TAURI_INTERNALS__?: {
    invoke: (cmd: string, args: Record<string, unknown>) => Promise<unknown>;
  };
  chrome?: {
    runtime?: {
      sendMessage: (msg: unknown) => Promise<unknown>;
      onMessage?: {
        addListener: (
          callback: (
            message: unknown,
            sender: unknown,
            sendResponse: (response?: unknown) => void,
          ) => void,
        ) => void;
      };
      getURL?: (path: string) => string;
      id?: string;
    };
    tabs?: { create: (options: { url: string }) => Promise<unknown> };
    identity?: {
      launchWebAuthFlow: (options: {
        url: string;
        interactive: boolean;
      }) => Promise<string>;
    };
  };
  electronAPI?: {
    openExternal?: (url: string) => Promise<void>;
  };
  acquireVsCodeApi?: () => {
    postMessage: (msg: unknown) => void;
  };
};

export type { PSAuthConfig } from "@pineseed/gui/types/auth";
type PSCasdoorConfig = PSAuthConfig["casdoor"];

/**
 * Auth Handler - Direct Casdoor Login + oRPC Integration
 * Handles login/signup directly with Casdoor UI, then uses JWT tokens for oRPC calls
 * Login flow: UI ↔ Casdoor (direct OAuth)
 * API calls: UI ↔ oRPC (with JWT tokens from Casdoor)
 * STRICT RULE: Business logic only, no UI code, no direct state mutations
 */

export interface AuthHandlerCallbacks {
  onAuthStateChange: (
    isAuthenticated: boolean,
    user: CasdoorUser | null,
    tokens: AuthTokens | null,
  ) => void;
  onError: (error: string) => void;
  onLoading: (loading: boolean) => void;
  onLoginUrlReceived: (url: string) => void;
}

export class AuthHandler {
  private static instance: AuthHandler;
  private callbacks: Partial<AuthHandlerCallbacks> = {};
  private casdoorConfig: PSCasdoorConfig;
  // private brandConfig: BrandConfig;
  private isInitialized = false;
  private processingCallback = false; // Flag to prevent duplicate callback processing

  private constructor(authConfig: PSAuthConfig) {
    this.casdoorConfig = authConfig.casdoor;
    // this.casdoorConfig = brandConfig.auth?.casdoor || {
    //   serverUrl: "http://localhost:8000",
    //   clientId: "demo_client",
    //   organizationName: "demo-org",
    //   appName: "demo-app",
    //   redirectPath: "/auth/callback",
    //   signinPath: "/api/auth/signin",
    //   ui: { mode: "redirect" as const },
    // };
  }

  public static getInstance(authConfig: PSAuthConfig): AuthHandler {
    if (!AuthHandler.instance) {
      AuthHandler.instance = new AuthHandler(authConfig);
    }
    return AuthHandler.instance;
  }

  /**
   * Initialize the auth handler with configuration
   */
  public async initialize(config: {
    casdoorConfig: Partial<PSCasdoorConfig>;
    callbacks?: AuthHandlerCallbacks;
  }): Promise<void> {
    // this.casdoorConfig = config.casdoorConfig;
    // if (config.casdoorConfig) {
    // this.casdoorConfig = {
    //   ...config.casdoorConfig,
    //   serverUrl: config.casdoorConfig.serverUrl || "",
    //   clientId: config.casdoorConfig.clientId || "",
    //   organizationName: config.casdoorConfig.organizationName || "",
    //   appName: config.casdoorConfig.appName || "",
    //   redirectPath: config.casdoorConfig.redirectPath || "",
    //   ui: {
    //     mode: config.casdoorConfig.ui?.mode || "redirect",
    //     theme: config.casdoorConfig.ui?.theme || "light",
    //     customization: config.casdoorConfig.ui?.customization || {},
    //   },
    // };
    // }

    if (config.callbacks) {
      this.setCallbacks(config.callbacks);
    }

    const win = window as ExtendedWindow;
    const isTauriMode = !!(
      win.__TAURI__ ||
      win.isTauri ||
      win.__TAURI_METADATA__
    );

    // Set up extension message listener for auth state changes
    if (win.__IVE_EXTENSION_MODE__) {
      this.setupExtensionMessageListener();
    }

    // Set up Tauri-specific listeners
    if (isTauriMode) {
      this.setupTauriListeners();
      // Also set up deep link listeners immediately for Tauri
      console.log(
        "🔐 Tauri mode detected during initialization, setting up deep link listeners...",
      );
      this.setupDeepLinkListeners();

      // Test deep link registration in debug mode
      // TODO: Remove this after testing
      this.testDeepLinkRegistration();
    }

    this.isInitialized = true;
  }

  /**
   * Set callback functions
   */
  public setCallbacks(callbacks: AuthHandlerCallbacks): void {
    this.callbacks = { ...callbacks };
  }

  /**
   * Handle deep link OAuth callback
   */
  public handleDeepLinkCallback(url: string): void {
    // Prevent duplicate processing of the same callback
    if (this.processingCallback) {
      console.log(
        "🔐 Tauri: OAuth callback already being processed, ignoring duplicate",
      );
      return;
    }

    try {
      const urlObj = new URL(url);
      const code = urlObj.searchParams.get("code");
      const state = urlObj.searchParams.get("state");

      if (code && state) {
        console.log(
          "🔐 Tauri: Deep link OAuth callback - Code:",
          code,
          "State:",
          state,
        );

        // Set processing flag to prevent duplicates
        this.processingCallback = true;

        // Handle callback asynchronously and ensure state updates are triggered
        this.handleCallback(code, state)
          .then((result) => {
            console.log(
              "🔐 Tauri: OAuth callback processed successfully, user:",
              result.user.email || result.user.name,
            );

            // Explicitly trigger auth state change callback to update UI
            this.callbacks.onAuthStateChange?.(
              true,
              result.user,
              result.tokens,
            );
          })
          .catch((error) => {
            console.error("🔐 Tauri: OAuth callback processing failed:", error);
            this.callbacks.onError?.(error.message || "Authentication failed");
          })
          .finally(() => {
            // Reset processing flag after completion (success or failure)
            this.processingCallback = false;
          });
      } else {
        const errorMsg = "Deep link missing OAuth parameters";
        console.error("🔐 Tauri:", errorMsg, url);
        this.callbacks.onError?.(errorMsg);
      }
    } catch (error) {
      const errorMsg = `Failed to parse deep link URL: ${error}`;
      console.error("🔐 Tauri:", errorMsg, url);
      this.callbacks.onError?.(errorMsg);
    }
  }

  /**
   * Setup OAuth callback listeners (URL params and deep links)
   */
  public setupOAuthCallbackListeners(): void {
    // Handle URL parameters for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");

    if (code && state) {
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      // Handle callback
      this.handleCallback(code, state);
    }

    // Setup deep link listeners
    this.setupDeepLinkListeners();
  }

  /**
   * Setup deep link listeners for OAuth callbacks
   */
  private setupDeepLinkListeners(): void {
    const handleDeepLinkAuthCallback = (event: Event) => {
      const customEvent = event as CustomEvent;
      const url = customEvent.detail;
      console.log("Handling deep link auth callback:", url);
      this.handleDeepLinkCallback(url);
    };

    // Add event listener for deep link callbacks (for web/extension)
    window.addEventListener(
      "deeplink-auth-callback",
      handleDeepLinkAuthCallback,
    );

    // Add Tauri event listener for deep link callbacks
    const win = window as ExtendedWindow;
    const isTauriMode = !!(
      win.__TAURI__ ||
      win.isTauri ||
      win.__TAURI_METADATA__
    );

    if (isTauriMode) {
      console.log("🔐 Setting up Tauri deep link event listener...");
      import("@tauri-apps/api/event")
        .then(({ listen }) => {
          console.log(
            "🔐 Tauri event API loaded, registering deeplink-auth-callback listener",
          );
          listen("deeplink-auth-callback", (event) => {
            const url = event.payload as string;
            console.log("🔐 Tauri deep link auth callback received:", url);
            handleDeepLinkAuthCallback(
              new CustomEvent("deeplink-auth-callback", { detail: url }),
            );
          }).catch((error) => {
            console.warn("Failed to listen for Tauri deep link events:", error);
          });
        })
        .catch((error) => {
          console.warn("Tauri event API not available:", error);
        });
    }
    // else {
    //   console.log(
    //     "🔐 Not in Tauri mode, skipping Tauri deep link listener setup",
    //   );
    // }
  }

  /**
   * Set up message listener for extension auth state changes
   */
  private async setupExtensionMessageListener(): Promise<void> {
    const extChrome = (window as ExtendedWindow).chrome;
    if (!extChrome?.runtime?.onMessage) {
      console.warn("Chrome runtime message API not available");
      return;
    }

    // Listen for auth state changes from background script
    extChrome.runtime.onMessage.addListener(
      async (message: unknown, _sender, _sendResponse) => {
        const extensionMessage = message as ExtensionMessage;
        if (extensionMessage?.type === "AUTH_STATE_CHANGED") {
          console.log(
            "🔐 Extension: Received auth state change from background:",
            !!extensionMessage.authState?.user,
          );

          if (extensionMessage.isAuthenticated && extensionMessage.authState) {
            // Store auth state locally
            this.storeAuthState(
              extensionMessage.authState.user,
              extensionMessage.authState.tokens,
            );

            // Notify callbacks to update UI state
            this.callbacks.onAuthStateChange?.(
              true,
              extensionMessage.authState.user,
              extensionMessage.authState.tokens,
            );
          } else {
            // Handle logout/auth clear
            await this.clearStoredAuthState();
            this.callbacks.onAuthStateChange?.(false, null, null);
          }
        }
      },
    );

    console.log("✅ Extension: Auth message listener set up");
  }

  /**
   * Set up Tauri-specific listeners for auth state management
   */
  private setupTauriListeners(): void {
    // Add window focus listener to recheck auth state when app regains focus
    // This handles the case where OAuth completes in browser and user returns to app
    const handleWindowFocus = async () => {
      console.log("🔐 Tauri: Window focus detected, rechecking auth state...");

      // Small delay to ensure any pending deep link callbacks are processed
      setTimeout(async () => {
        try {
          const authStatus = await this.checkAuthStatus();
          if (authStatus.isAuthenticated && authStatus.user) {
            console.log(
              "🔐 Tauri: Auth state found on focus, user:",
              authStatus.user.email || authStatus.user.name,
            );
          } else {
            console.log("🔐 Tauri: No auth state found on focus");
          }
        } catch (error) {
          console.warn(
            "🔐 Tauri: Failed to recheck auth state on focus:",
            error,
          );
        }
      }, 500); // 500ms delay to allow deep link processing
    };

    window.addEventListener("focus", handleWindowFocus);

    // Also check when the window becomes visible (for cases where focus doesn't fire)
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        handleWindowFocus();
      }
    });

    console.log("✅ Tauri: Window focus and visibility listeners set up");
  }

  /**
   * Test deep link registration (debug mode only)
   */
  private async testDeepLinkRegistration(): Promise<void> {
    try {
      console.log("🔐 Testing deep link registration...");

      // Try to invoke the test command
      const { invoke } = await import("@tauri-apps/api/core");
      const result = (await invoke("test_deep_link_registration")) as string;
      console.log("🔐 Deep link registration test results:");
      console.log(result);
    } catch (error) {
      console.warn("🔐 Failed to test deep link registration:", error);
    }
  }

  /**
   * Generate login URL for Casdoor OAuth with PKCE
   */
  public async getLoginUrl(state?: string): Promise<string> {
    this.ensureInitialized();

    try {
      this.callbacks.onLoading?.(true);

      // Generate PKCE challenge for security
      const pkce = await this.generatePKCEChallenge();
      const casdoorConfig = this.casdoorConfig;
      // console.dir({ casdoorConfig }, { depth: null });

      // Store PKCE verifier for later use in token exchange
      // Use secure storage instead of localStorage
      await storePKCEVerifier(secureStorage, pkce.codeVerifier);

      // Extension mode: delegate OAuth to background script
      if ((window as ExtendedWindow).__IVE_EXTENSION_MODE__) {
        const extChrome = (window as ExtendedWindow).chrome;
        if (!extChrome?.runtime?.sendMessage) {
          throw new Error("Chrome runtime not available");
        }

        console.log(
          "🔐 Extension: Starting OAuth flow via background script...",
        );

        // Send OAuth start request to background script
        const authResult = (await extChrome.runtime.sendMessage({
          type: "START_OAUTH",
          casdoorConfig: casdoorConfig,
          pkce: pkce,
          state: state || "default",
        })) as {
          success: boolean;
          user?: CasdoorUser;
          tokens?: AuthTokens;
          error?: string;
        };

        if (!authResult.success || !authResult.user || !authResult.tokens) {
          throw new Error(authResult.error || "Authentication failed");
        }

        // Store auth state and notify callbacks
        this.storeAuthState(authResult.user, authResult.tokens);
        this.callbacks.onAuthStateChange?.(
          true,
          authResult.user,
          authResult.tokens,
        );

        return "extension-oauth-complete"; // Return something for compatibility
      }

      // Web mode: use traditional OAuth flow
      // For Tauri, use deep link URI; for web, use traditional redirect
      const win = window as ExtendedWindow;
      const isTauriMode = !!(
        win.__TAURI__ ||
        win.isTauri ||
        win.__TAURI_METADATA__
      );
      const redirectUri = isTauriMode
        ? "ive://auth/callback"
        : `${window.location.origin}${casdoorConfig.redirectPath}`;

      // console.log("🔐 Using redirect URI:", redirectUri);

      const loginUrl = new URL(
        `${casdoorConfig.serverUrl}/login/oauth/authorize`,
      );

      loginUrl.searchParams.set("client_id", casdoorConfig.clientId);
      loginUrl.searchParams.set("response_type", "code");
      loginUrl.searchParams.set("redirect_uri", redirectUri);
      loginUrl.searchParams.set("scope", "openid profile email");
      loginUrl.searchParams.set("state", state || "default");

      // Add PKCE parameters
      loginUrl.searchParams.set("code_challenge", pkce.codeChallenge);
      loginUrl.searchParams.set("code_challenge_method", "S256");

      // Add organization and application parameters for Casdoor
      if (casdoorConfig.organizationName) {
        loginUrl.searchParams.set(
          "organization",
          casdoorConfig.organizationName,
        );
      }
      if (casdoorConfig.appName) {
        loginUrl.searchParams.set("application", casdoorConfig.appName);
      }

      const authUrl = loginUrl.toString();
      console.log("Generated login URL:", authUrl);
      this.callbacks.onLoginUrlReceived?.(authUrl);

      return authUrl;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to generate login URL";
      this.callbacks.onError?.(errorMessage);
      throw error;
    } finally {
      this.callbacks.onLoading?.(false);
    }
  }

  /**
   * Generate signup URL for Casdoor OAuth with PKCE
   */
  public async getSignupUrl(state?: string): Promise<string> {
    this.ensureInitialized();

    try {
      this.callbacks.onLoading?.(true);

      // Generate PKCE challenge for security
      const pkce = await this.generatePKCEChallenge();
      const casdoorConfig = this.casdoorConfig;

      // Store PKCE verifier for later use in token exchange
      await storePKCEVerifier(secureStorage, pkce.codeVerifier);

      // For CLI/non-browser contexts, use simple redirect
      const win = window as ExtendedWindow;
      const isTauriMode = !!(
        win.__TAURI__ ||
        win.isTauri ||
        win.__TAURI_METADATA__
      );
      const redirectUri = isTauriMode
        ? "ive://auth/callback"
        : `${window.location.origin}${casdoorConfig.redirectPath}`;

      // console.log("🔐 Using redirect URI:", redirectUri);

      const signupUrl = new URL(
        `${casdoorConfig.serverUrl}/signup/oauth/authorize`,
      );

      signupUrl.searchParams.set("client_id", casdoorConfig.clientId);
      signupUrl.searchParams.set("response_type", "code");
      signupUrl.searchParams.set("redirect_uri", redirectUri);
      signupUrl.searchParams.set("scope", "openid profile email");
      signupUrl.searchParams.set("state", state || "default");

      // Add PKCE parameters
      signupUrl.searchParams.set("code_challenge", pkce.codeChallenge);
      signupUrl.searchParams.set("code_challenge_method", "S256");

      // Add organization and application parameters for Casdoor
      if (casdoorConfig.organizationName) {
        signupUrl.searchParams.set(
          "organization",
          casdoorConfig.organizationName,
        );
      }
      if (casdoorConfig.appName) {
        signupUrl.searchParams.set("application", casdoorConfig.appName);
      }

      const authUrl = signupUrl.toString();
      console.log("Generated signup URL:", authUrl);
      this.callbacks.onLoginUrlReceived?.(authUrl);

      return authUrl;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to generate signup URL";
      this.callbacks.onError?.(errorMessage);
      throw error;
    } finally {
      this.callbacks.onLoading?.(false);
    }
  }

  /**
   * Handle OAuth callback from Casdoor
   */
  public async handleCallback(
    code: string,
    state: string,
    participantId?: string,
  ): Promise<{ user: CasdoorUser; tokens: AuthTokens }> {
    this.ensureInitialized();

    try {
      this.callbacks.onLoading?.(true);

      // Exchange code for tokens directly with Casdoor
      const tokenResponse = await this.exchangeCodeForTokens(code, state);

      // Get user info from Casdoor using the access token
      const user = await this.getCasdoorUserInfo(tokenResponse.access_token);

      const tokens: AuthTokens = {
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token,
        expiresIn: tokenResponse.expires_in,
        tokenType: tokenResponse.token_type || "Bearer",
        scope: tokenResponse.scope || "openid profile email",
      };

      // Link participant to user if participantId provided
      if (participantId) {
        try {
          await this.linkParticipantToUser(
            participantId,
            user.id,
            tokens.accessToken,
          );
        } catch (error) {
          console.warn("Failed to link participant to user:", error);
          // Don't fail auth flow if linking fails
        }
      }

      // Store auth state
      this.storeAuthState(user, tokens);

      // Notify callbacks
      this.callbacks.onAuthStateChange?.(true, user, tokens);

      return { user, tokens };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Authentication failed";
      this.callbacks.onError?.(errorMessage);
      throw error;
    } finally {
      this.callbacks.onLoading?.(false);
    }
  }

  /**
   * Link participant to user via identifier API
   */
  private async linkParticipantToUser(
    participantId: string,
    userId: string,
    accessToken: string,
  ): Promise<void> {
    const apiUrl = this.casdoorConfig.serverUrl.replace(/\/api$/, "");
    const response = await fetch(`${apiUrl}/api/identifier/link`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": accessToken,
      },
      body: JSON.stringify({
        participantId,
        userId,
        linkSource: "login",
        deviceInfo: {
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : "server",
          platform:
            typeof navigator !== "undefined" ? navigator.platform : "server",
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to link participant");
    }
  }

  /**
   * Get current user information using stored token
   */
  public async getCurrentUser(): Promise<CasdoorUser> {
    this.ensureInitialized();

    try {
      const storedAuth = await this.loadStoredAuthState();
      if (!storedAuth) {
        throw new Error("No stored authentication");
      }

      this.callbacks.onLoading?.(true);

      // Get fresh user info from Casdoor
      const user = await this.getCasdoorUserInfo(storedAuth.tokens.accessToken);

      // Update stored user info
      this.storeAuthState(user, storedAuth.tokens);

      // Notify callbacks
      this.callbacks.onAuthStateChange?.(true, user, storedAuth.tokens);

      return user;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to get user information";
      this.callbacks.onError?.(errorMessage);

      // Clear invalid auth state
      this.clearStoredAuthState();
      this.callbacks.onAuthStateChange?.(false, null, null);

      throw error;
    } finally {
      this.callbacks.onLoading?.(false);
    }
  }

  /**
   * Verify token with Casdoor directly
   */
  public async verifyToken(token: string): Promise<boolean> {
    this.ensureInitialized();

    try {
      const response = await fetch(
        `${this.casdoorConfig.serverUrl}/api/get-account`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      return response.ok;
    } catch (error) {
      console.warn("Token verification failed:", error);
      return false;
    }
  }

  /**
   * Refresh access token using refresh token directly with Casdoor
   */
  public async refreshToken(refreshToken: string): Promise<AuthTokens> {
    this.ensureInitialized();

    try {
      this.callbacks.onLoading?.(true);

      const response = await fetch(
        `${this.casdoorConfig.serverUrl}/api/login/oauth/refresh_token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            grant_type: "refresh_token",
            refresh_token: refreshToken,
            client_id: this.casdoorConfig.clientId,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const tokenData = await response.json();

      const tokens: AuthTokens = {
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token || refreshToken, // Keep old refresh token if new one not provided
        expiresIn: tokenData.expires_in,
        tokenType: tokenData.token_type || "Bearer",
        scope: tokenData.scope || "openid profile email",
      };

      // Update stored tokens
      const storedAuth = await this.loadStoredAuthState();
      if (storedAuth) {
        this.storeAuthState(storedAuth.user, tokens);
      }

      return tokens;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to refresh token";
      this.callbacks.onError?.(errorMessage);

      // Clear invalid auth state
      this.clearStoredAuthState();
      this.callbacks.onAuthStateChange?.(false, null, null);

      throw error;
    } finally {
      this.callbacks.onLoading?.(false);
    }
  }

  /**
   * Logout user and revoke tokens
   */
  public async logout(token?: string): Promise<void> {
    this.ensureInitialized();

    try {
      this.callbacks.onLoading?.(true);

      const authToken =
        token || (await this.loadStoredAuthState())?.tokens.accessToken;

      if (authToken) {
        // Attempt to revoke token with Casdoor
        try {
          await fetch(`${this.casdoorConfig.serverUrl}/api/logout`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          });
        } catch (error) {
          console.warn("Failed to revoke token with Casdoor:", error);
          // Continue with local logout even if remote logout fails
        }
      }

      // Clear local auth state
      this.clearStoredAuthState();

      // Notify callbacks
      this.callbacks.onAuthStateChange?.(false, null, null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Logout failed";
      this.callbacks.onError?.(errorMessage);
      throw error;
    } finally {
      this.callbacks.onLoading?.(false);
    }
  }

  /**
   * Check current authentication status
   */
  public async checkAuthStatus(): Promise<{
    isAuthenticated: boolean;
    user: CasdoorUser | null;
  }> {
    try {
      const win = window as ExtendedWindow;
      const isTauriMode = !!(
        win.__TAURI__ ||
        win.isTauri ||
        win.__TAURI_METADATA__
      );
      const isExtensionMode = !!win.__IVE_EXTENSION_MODE__;

      // In extension mode, check background script first
      if (isExtensionMode) {
        console.log("🔐 Extension: checkAuthStatus called in extension mode");
        const extChrome = win.chrome;
        if (extChrome?.runtime?.sendMessage) {
          try {
            console.log("🔐 Extension: Checking auth state from background...");
            const response = (await extChrome.runtime.sendMessage({
              type: "GET_AUTH_STATE",
            })) as { authState?: { user: CasdoorUser; tokens: AuthTokens } };

            console.log(
              "🔐 Extension: Background response:",
              !!response?.authState,
            );
            if (response.authState) {
              console.log(
                "🔐 Extension: Found auth state in background:",
                !!response.authState.user,
              );
              // Update local state with extension auth state
              this.storeAuthState(
                response.authState.user,
                response.authState.tokens,
              );
              this.callbacks.onAuthStateChange?.(
                true,
                response.authState.user,
                response.authState.tokens,
              );
              return { isAuthenticated: true, user: response.authState.user };
            } else {
              console.log("🔐 Extension: No auth state found in background");
            }
          } catch (error) {
            console.warn(
              "🔐 Extension: Failed to get auth state from background:",
              error,
            );
          }
        } else {
          console.warn(
            "🔐 Extension: chrome.runtime.sendMessage not available",
          );
        }
      } else if (isTauriMode) {
        console.log("🔐 Tauri: checkAuthStatus called in Tauri mode");
      } else {
        console.log("🔐 Web: checkAuthStatus called in web mode");
      }

      // Check local storage for auth state (works for all modes)
      const storedAuth = await this.loadStoredAuthState();
      if (!storedAuth) {
        console.log(
          `🔐 ${isTauriMode ? "Tauri" : isExtensionMode ? "Extension" : "Web"}: No local auth state found`,
        );
        return { isAuthenticated: false, user: null };
      }

      // Verify token is still valid
      try {
        const isValid = await this.verifyToken(storedAuth.tokens.accessToken);
        if (!isValid) {
          // Try to refresh token
          if (storedAuth.tokens.refreshToken) {
            const newTokens = await this.refreshToken(
              storedAuth.tokens.refreshToken,
            );

            // Trigger callback with refreshed tokens
            this.callbacks.onAuthStateChange?.(
              true,
              storedAuth.user,
              newTokens,
            );

            return { isAuthenticated: true, user: storedAuth.user };
          } else {
            console.log(
              `🔐 ${isTauriMode ? "Tauri" : isExtensionMode ? "Extension" : "Web"}: No refresh token available, clearing auth state`,
            );
            await this.clearStoredAuthState();
            return { isAuthenticated: false, user: null };
          }
        }

        // Token is valid, trigger callback to update UI state
        this.callbacks.onAuthStateChange?.(
          true,
          storedAuth.user,
          storedAuth.tokens,
        );

        return { isAuthenticated: true, user: storedAuth.user };
      } catch (tokenError) {
        console.warn(
          `🔐 ${isTauriMode ? "Tauri" : isExtensionMode ? "Extension" : "Web"}: Token verification failed:`,
          tokenError,
        );
        // If token verification fails, try refresh
        if (storedAuth.tokens.refreshToken) {
          try {
            const newTokens = await this.refreshToken(
              storedAuth.tokens.refreshToken,
            );
            console.log(
              `🔐 ${isTauriMode ? "Tauri" : isExtensionMode ? "Extension" : "Web"}: Token refresh successful after verification failure`,
            );

            // Trigger callback with refreshed tokens
            this.callbacks.onAuthStateChange?.(
              true,
              storedAuth.user,
              newTokens,
            );

            return { isAuthenticated: true, user: storedAuth.user };
          } catch (refreshError) {
            console.warn(
              `🔐 ${isTauriMode ? "Tauri" : isExtensionMode ? "Extension" : "Web"}: Token refresh failed:`,
              refreshError,
            );
            await this.clearStoredAuthState();
            return { isAuthenticated: false, user: null };
          }
        } else {
          console.log(
            `🔐 ${isTauriMode ? "Tauri" : isExtensionMode ? "Extension" : "Web"}: No refresh token available after verification failure, clearing auth state`,
          );
          this.clearStoredAuthState();
          return { isAuthenticated: false, user: null };
        }
      }
    } catch (error) {
      console.warn("Auth status check failed:", error);
      return { isAuthenticated: false, user: null };
    }
  }

  /**
   * Exchange authorization code for tokens directly with Casdoor using PKCE
   */
  private async exchangeCodeForTokens(
    code: string,
    state: string,
  ): Promise<{
    access_token: string;
    refresh_token?: string;
    expires_in: number;
    token_type?: string;
    scope?: string;
  }> {
    const casdoorConfig = this.casdoorConfig;
    // MUST match the redirect URI used in getLoginUrl!
    const win = window as ExtendedWindow;
    const isTauriMode = !!(
      win.__TAURI__ ||
      win.isTauri ||
      win.__TAURI_METADATA__
    );
    const redirectUri = win.__IVE_EXTENSION_MODE__
      ? `${window.location.origin}/auth/callback.html`
      : isTauriMode
        ? "ive://auth/callback"
        : `${window.location.origin}${casdoorConfig.redirectPath}`;

    // console.dir({ casdoorConfig }, { depth: null });
    // console.log("🔐 Token exchange using redirect URI:", redirectUri);

    // Retrieve PKCE verifier from secure storage
    const codeVerifier = await getPKCEVerifier(secureStorage);
    console.log("🔐 PKCE verifier found:", !!codeVerifier);

    if (!codeVerifier) {
      throw new Error(
        "PKCE code verifier not found - please try logging in again",
      );
    }

    console.log("🔐 Using PKCE verifier, length:", codeVerifier.length);

    // Clean up stored verifier
    await clearPKCEVerifier(secureStorage);

    const response = await fetch(
      `${casdoorConfig.serverUrl}/api/login/oauth/access_token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          grant_type: "authorization_code",
          client_id: casdoorConfig.clientId,
          code: code,
          redirect_uri: redirectUri,
          state: state,
          code_verifier: codeVerifier,
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get user info from Casdoor directly
   */
  private async getCasdoorUserInfo(accessToken: string): Promise<CasdoorUser> {
    const response = await fetch(
      `${this.casdoorConfig.serverUrl}/api/get-account`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to get user info: ${response.status}`);
    }

    const userData = await response.json();

    // Convert Casdoor user format to our format
    return this.convertCasdoorUserToUI(userData);
  }

  /**
   * Convert Casdoor user data to UI format
   */
  private convertCasdoorUserToUI(casdoorUser: {
    owner?: string;
    name: string;
    createdTime?: string;
    updatedTime?: string;
    id?: string;
    type?: string;
    displayName?: string;
    email: string;
    avatar?: string;
    phone?: string;
    isAdmin?: boolean;
    isGlobalAdmin?: boolean;
    isForbidden?: boolean;
    isDeleted?: boolean;
    signupApplication?: string;
    properties?: Record<string, string>;
    lastSigninTime?: string;
  }): CasdoorUser {
    return {
      owner: casdoorUser.owner || "built-in",
      name: casdoorUser.name,
      createdTime: casdoorUser.createdTime
        ? new Date(casdoorUser.createdTime)
        : new Date(),
      updatedTime: casdoorUser.updatedTime
        ? new Date(casdoorUser.updatedTime)
        : new Date(),
      id: casdoorUser.id || casdoorUser.name,
      type: casdoorUser.type || "normal-user",
      displayName: casdoorUser.displayName || casdoorUser.name,
      email: casdoorUser.email,
      avatar: casdoorUser.avatar,
      phone: casdoorUser.phone,
      isAdmin: casdoorUser.isAdmin || false,
      isGlobalAdmin: casdoorUser.isGlobalAdmin || false,
      isForbidden: casdoorUser.isForbidden || false,
      isDeleted: casdoorUser.isDeleted || false,
      signupApplication: casdoorUser.signupApplication,
      properties: casdoorUser.properties || {},
      lastSigninTime: casdoorUser.lastSigninTime
        ? new Date(casdoorUser.lastSigninTime)
        : undefined,
    };
  }

  /**
   * Generate PKCE challenge for enhanced security
   */
  public async generatePKCEChallenge(): Promise<{
    codeVerifier: string;
    codeChallenge: string;
  }> {
    const codeVerifier = this.generateRandomString(128);

    // Create SHA256 hash of the code verifier
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);

    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    // Convert hash to base64url
    const hashArray = new Uint8Array(hashBuffer);
    const base64String = btoa(String.fromCharCode(...hashArray));
    const codeChallenge = base64String
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");

    return { codeVerifier, codeChallenge };
  }

  private generateRandomString(length: number): string {
    const charset =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
    let result = "";
    const values = crypto.getRandomValues(new Uint8Array(length));

    for (let i = 0; i < length; i++) {
      result += charset[values[i] % charset.length];
    }

    return result;
  }

  /**
   * Store authentication state in secure storage
   */
  public async storeAuthState(
    user: CasdoorUser,
    tokens: AuthTokens,
  ): Promise<void> {
    try {
      const authState = {
        user: {
          ...user,
          createdTime: user.createdTime.toISOString(),
          updatedTime: user.updatedTime.toISOString(),
        },
        tokens,
        timestamp: Date.now(),
      };

      await secureStorage.setItem(
        SecureStorageKeys.AUTH_STATE,
        JSON.stringify(authState),
      );
      // await storeAuthTokens(tokens.accessToken, tokens.refreshToken);
    } catch (error) {
      console.warn("Failed to store auth state:", error);
    }
  }

  /**
   * Load stored authentication state
   */
  public async loadStoredAuthState(): Promise<{
    user: CasdoorUser;
    tokens: AuthTokens;
  } | null> {
    try {
      const stored = await secureStorage.getItem(SecureStorageKeys.AUTH_STATE);
      if (!stored) return null;

      const authState = JSON.parse(stored);

      // Convert date strings back to Date objects
      const user: CasdoorUser = {
        ...authState.user,
        createdTime: new Date(authState.user.createdTime),
        updatedTime: new Date(authState.user.updatedTime),
      };

      return {
        user,
        tokens: authState.tokens,
      };
    } catch (error) {
      console.warn("Failed to load stored auth state:", error);
      return null;
    }
  }

  /**
   * Clear stored authentication state
   */
  public async clearStoredAuthState(): Promise<void> {
    try {
      await clearAuthTokens(secureStorage);
    } catch (error) {
      console.warn("Failed to clear auth state:", error);
    }
  }

  /**
   * Get access token for API calls
   */
  public async getAccessToken(): Promise<string | null> {
    const { accessToken } = await getAuthTokens(secureStorage);
    return accessToken;
  }

  /**
   * Cleanup handler resources
   */
  public cleanup(): void {
    this.callbacks = {};
  }

  private ensureInitialized(): void {
    if (!this.isInitialized) {
      throw new Error("AuthHandler must be initialized before use");
    }
  }

  public isReady(): boolean {
    return this.isInitialized;
  }

  public getCasdoorConfig(): PSCasdoorConfig {
    return this.casdoorConfig;
  }

  /**
   * Login via popup window (OAuth 2.0 Authorization Code Flow)
   * Returns a promise that resolves when authentication is complete
   */
  public async loginWithPopup(
    state?: string,
  ): Promise<{ user: CasdoorUser; tokens: AuthTokens }> {
    this.ensureInitialized();

    const loginUrl = await this.getLoginUrl(state);

    return new Promise((resolve, reject) => {
      try {
        this.callbacks.onLoading?.(true);

        // Open popup window
        const popup = window.open(
          loginUrl,
          "CasdoorLogin",
          "width=600,height=700,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no",
        );

        if (!popup) {
          throw new Error("Popup blocked. Please allow popups for this site.");
        }

        // Monitor popup window
        const checkPopup = setInterval(() => {
          if (!popup || popup.closed) {
            clearInterval(checkPopup);
            this.callbacks.onLoading?.(false);
            reject(new Error("Authentication cancelled by user"));
            return;
          }

          try {
            // Check if popup has navigated to redirect URI
            const popupUrl = popup.location.href;
            const redirectUri = `${window.location.origin}${this.casdoorConfig.redirectPath}`;

            if (popupUrl.startsWith(redirectUri)) {
              // Extract authorization code from URL
              const urlParams = new URLSearchParams(new URL(popupUrl).search);
              const code = urlParams.get("code");
              const returnedState = urlParams.get("state");

              if (code && returnedState) {
                clearInterval(checkPopup);
                popup.close();

                // Exchange code for tokens
                this.handleCallback(code, returnedState)
                  .then((result) => {
                    this.callbacks.onLoading?.(false);
                    resolve(result);
                  })
                  .catch((error) => {
                    this.callbacks.onLoading?.(false);
                    reject(error);
                  });
              }
            }
          } catch (error) {
            console.error("🔐 Popup error:", error);
            // Cross-origin error is expected until popup navigates to our domain
            // Continue monitoring
          }
        }, 1000);

        // Set up message listener for cross-domain communication
        const messageListener = (event: MessageEvent) => {
          // Verify origin for security
          if (event.origin !== window.location.origin) {
            return;
          }

          const { code, state: returnedState, error } = event.data;

          if (error) {
            clearInterval(checkPopup);
            window.removeEventListener("message", messageListener);
            popup?.close();
            this.callbacks.onLoading?.(false);
            reject(new Error(error));
            return;
          }

          if (code && returnedState) {
            clearInterval(checkPopup);
            window.removeEventListener("message", messageListener);
            popup?.close();

            // Exchange code for tokens
            this.handleCallback(code, returnedState)
              .then((result) => {
                this.callbacks.onLoading?.(false);
                resolve(result);
              })
              .catch((error) => {
                this.callbacks.onLoading?.(false);
                reject(error);
              });
          }
        };

        window.addEventListener("message", messageListener);

        // Cleanup timeout
        setTimeout(() => {
          if (popup && !popup.closed) {
            clearInterval(checkPopup);
            window.removeEventListener("message", messageListener);
            popup.close();
            this.callbacks.onLoading?.(false);
            reject(new Error("Authentication timeout"));
          }
        }, 300000); // 5 minute timeout
      } catch (error) {
        this.callbacks.onLoading?.(false);
        const errorMessage =
          error instanceof Error ? error.message : "Popup login failed";
        this.callbacks.onError?.(errorMessage);
        reject(error);
      }
    });
  }

  /**
   * Redirect to login URL
   */
  public async redirectToLogin(state?: string): Promise<void> {
    const loginUrl = await this.getLoginUrl(state);
    console.log("🔐 Generated login URL:", loginUrl);

    // Normalise window with extended properties
    const win = window as unknown as ExtendedWindow;

    // Debug environment detection
    const isTauri = !!(win.__TAURI__ || win.isTauri || win.__TAURI_METADATA__);
    console.log("🔍 Environment detection:");
    console.log("  - __IVE_EXTENSION_MODE__:", !!win.__IVE_EXTENSION_MODE__);
    console.log("  - __TAURI__:", !!win.__TAURI__);
    console.log("  - isTauri:", !!win.isTauri);
    console.log("  - __TAURI_METADATA__:", !!win.__TAURI_METADATA__);
    console.log("  - Combined Tauri detection:", isTauri);
    console.log("  - electronAPI:", !!win.electronAPI);
    console.log("  - acquireVsCodeApi:", !!win.acquireVsCodeApi);
    console.log("  - User Agent:", navigator.userAgent);

    // 1. Chrome Extension mode ------------------------------------------------
    if (win.__IVE_EXTENSION_MODE__) {
      console.log("🔐 Using Chrome Extension mode");
      const extChrome = win.chrome;

      // Try creating the tab directly first (simpler, avoids message round-trip)
      if (extChrome?.tabs?.create) {
        console.log("chrome.tabs.create");
        try {
          await extChrome.tabs.create({ url: loginUrl });
          win.close?.();
          return;
        } catch (err) {
          console.warn("chrome.tabs.create failed", err);
        }
      }

      // Fallback to background message (older MV3 behaviour)
      if (extChrome?.runtime?.sendMessage) {
        console.log("chrome.runtime.sendMessage");
        try {
          await extChrome.runtime.sendMessage({
            type: "OPEN_EXTERNAL_URL",
            url: loginUrl,
          });
          win.close?.();
          return;
        } catch (err) {
          console.warn("Extension message OPEN_EXTERNAL_URL failed", err);
        }
      }
    }

    // 2. Tauri desktop build ---------------------------------------------------
    if (isTauri) {
      console.log("🔐 Using Tauri mode - opening system browser");
      try {
        this.callbacks.onLoading?.(true);

        // Try multiple approaches to invoke Tauri command
        let success = false;

        // Method 1: Try @tauri-apps/api/core
        try {
          const tauriCore = (await import(
            /* @vite-ignore */ "@tauri-apps/api/core"
          )) as {
            invoke: (
              cmd: string,
              args: Record<string, unknown>,
            ) => Promise<unknown>;
          };

          if (tauriCore?.invoke) {
            console.log("🔐 Method 1: Using @tauri-apps/api/core");
            await tauriCore.invoke("open_external_url", { url: loginUrl });
            success = true;
          }
        } catch (err) {
          console.warn("Method 1 failed:", err);
        }

        // Method 2: Try global __TAURI_INTERNALS__ (fallback)
        if (!success && win.__TAURI_INTERNALS__) {
          try {
            console.log("🔐 Method 2: Using __TAURI_INTERNALS__");
            await win.__TAURI_INTERNALS__.invoke("open_external_url", {
              url: loginUrl,
            });
            success = true;
          } catch (err) {
            console.warn("Method 2 failed:", err);
          }
        }

        // // Method 3: Try @tauri-apps/api/shell as last resort
        // if (!success) {
        //   try {
        //     console.log("🔐 Method 3: Using @tauri-apps/api/shell");
        //     const { open } = await import("@tauri-apps/api/shell");
        //     await open(loginUrl);
        //     success = true;
        //   } catch (err) {
        //     console.warn("Method 3 failed:", err);
        //   }
        // }

        if (success) {
          console.log("✅ Tauri: System browser opened successfully");
          console.log("🔐 Tauri: Waiting for OAuth callback via deep link...");
          return;
        } else {
          throw new Error("All Tauri methods failed");
        }
      } catch (err) {
        console.error("❌ Tauri open_external_url failed:", err);
        this.callbacks.onError?.("Failed to open system browser");
        // Don't fall through - we want to know if Tauri fails
        throw err;
      } finally {
        this.callbacks.onLoading?.(false);
      }
    }

    // 3. Electron / VSCode webview -------------------------------------------
    if (win.electronAPI?.openExternal) {
      console.log("🔐 Using Electron mode");
      try {
        await win.electronAPI.openExternal(loginUrl);
        return;
      } catch (err) {
        console.warn("electronAPI.openExternal failed", err);
      }
    }

    const vscodeApi = win.acquireVsCodeApi?.();
    if (vscodeApi) {
      console.log("🔐 Using VSCode mode");
      try {
        vscodeApi.postMessage({ type: "OPEN_EXTERNAL_URL", url: loginUrl });
        return;
      } catch (err) {
        console.warn("VSCode postMessage OPEN_EXTERNAL_URL failed", err);
      }
    }

    // 4. Fallback: traditional navigation (web build)
    console.log("🔐 Using web fallback - navigating in current window");
    console.warn(
      "⚠️ No platform-specific URL opening method found, falling back to window navigation",
    );
    win.location.href = loginUrl;
  }

  /**
   * Sign out (alias for logout)
   */
  public async signOut(): Promise<void> {
    return this.logout();
  }

  /**
   * Get user info (alias for getCurrentUser)
   */
  public async getUserInfo(): Promise<CasdoorUser> {
    return this.getCurrentUser();
  }
}

// Export singleton instance
// export const authHandler = AuthHandler.getInstance();
